// src/context/ThemeContext.js — Theme / dark mode / density provider
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { STORAGE_KEYS, TWEAK_DEFAULTS } from '../config/env';

const STORAGE_KEY = STORAGE_KEYS.tweaks;

const ThemeContext = createContext(null);

function accentContrast(accent) {
  const hex = String(accent || '').replace('#', '');
  if (!/^[\da-f]{6}$/i.test(hex)) return '#ffffff';
  const channels = [0, 2, 4].map(index => parseInt(hex.slice(index, index + 2), 16) / 255)
    .map(channel => (channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4));
  const luminance = 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
  const whiteContrast = 1.05 / (luminance + 0.05);
  const darkContrast = (luminance + 0.05) / 0.05;
  return whiteContrast >= darkContrast ? '#ffffff' : '#111827';
}

export function ThemeProvider({ children }) {
  const [tweaks, setTweaks] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return { ...TWEAK_DEFAULTS, ...JSON.parse(raw) };
    } catch (e) { /* ignore */ }
    return TWEAK_DEFAULTS;
  });

  // Persist to localStorage on change
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(tweaks)); } catch (e) { /* ignore */ }
  }, [tweaks]);

  const setTweak = useCallback((key, value) => {
    setTweaks(prev => {
      if (typeof key === 'object') return { ...prev, ...key };
      return { ...prev, [key]: value };
    });
  }, []);

  // CSS class for root element
  const appClass = useMemo(
    () => `app density-${tweaks.density} ${tweaks.dark ? 'dark' : 'light'}`,
    [tweaks.density, tweaks.dark],
  );

  // Root theme variables are consumed by the dashboard and shared shell.
  const themeStyle = useMemo(
    () => ({
      '--accent': tweaks.accent,
      '--accent-contrast': accentContrast(tweaks.accent),
    }),
    [tweaks.accent],
  );

  return (
    <ThemeContext.Provider value={{ tweaks, setTweak, appClass, themeStyle }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useThemeContext() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useThemeContext must be used within ThemeProvider');
  return ctx;
}
